import { WORKSPACE_PRODUCT_IDENTITY_COL_CLASS } from '@/components/workspace/WorkspaceProductIdentityCell';
import { WORKSPACE_METRIC_COLUMNS, type WorkspaceMetricColumnDef } from '@/utils/workspaceAdProductMetrics';

export type WorkspaceEntityColumnDef = {
  id: string;
  label: string;
  className?: string;
  /** Resize floor in px when the content needs more than the default minimum. */
  minWidth?: number;
  /** Explanation shown from an ⓘ button next to the heading. */
  info?: string;
};

export const PORTFOLIO_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'name', label: 'Name' },
  { id: 'state', label: 'State' },
  { id: 'outOfBudgetBoost', label: 'Out of Budget' },
];

/** Demo-only budget column (editable); not shown for live accounts. */
export const DEMO_PORTFOLIO_BUDGET_COLUMN: WorkspaceEntityColumnDef = {
  id: 'budget',
  label: 'Budget',
};

export const CAMPAIGN_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'name', label: 'Campaign' },
  { id: 'adProduct', label: 'Type' },
  { id: 'state', label: 'State' },
  { id: 'budget', label: 'Budget' },
  { id: 'autoBidding', label: 'Auto Bidding' },
  { id: 'outOfBudgetBoost', label: 'Out of Budget' },
];

export const AD_GROUP_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'name', label: 'Ad group' },
  { id: 'adProduct', label: 'Type' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'state', label: 'State' },
];

/** Amazon entity id columns hidden for demo accounts. */
export const DEMO_HIDDEN_ENTITY_ID_COLUMNS = new Set([
  'portfolioId',
  'campaignId',
  'adGroupId',
]);

export function entityColumnsForDemo(
  columns: WorkspaceEntityColumnDef[],
  isDemo: boolean
): WorkspaceEntityColumnDef[] {
  if (!isDemo) return columns;
  return columns.filter((c) => !DEMO_HIDDEN_ENTITY_ID_COLUMNS.has(c.id));
}

export function portfolioEntityColumnsForDemo(isDemo: boolean): WorkspaceEntityColumnDef[] {
  const base = entityColumnsForDemo(PORTFOLIO_ENTITY_COLUMNS, isDemo);
  if (!isDemo) return base;
  // Insert budget after name (before state).
  const nameIdx = base.findIndex((c) => c.id === 'name');
  if (nameIdx < 0) return [...base, DEMO_PORTFOLIO_BUDGET_COLUMN];
  return [...base.slice(0, nameIdx + 1), DEMO_PORTFOLIO_BUDGET_COLUMN, ...base.slice(nameIdx + 1)];
}

export const KEYWORD_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'keywordText', label: 'Keyword' },
  { id: 'adProduct', label: 'Type' },
  { id: 'matchType', label: 'Match' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'state', label: 'State' },
  {
    id: 'bid',
    label: 'Bid',
    minWidth: 128,
    info: "Amazon's suggested bid for this keyword: recommended · minimum – maximum.",
  },
];

export const AD_PRODUCT_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'product', label: 'Product', className: WORKSPACE_PRODUCT_IDENTITY_COL_CLASS },
  { id: 'campaign', label: 'Campaign' },
  { id: 'agAsins', label: 'AG ASINs' },
  { id: 'status', label: 'Status' },
];

export const ADVERTISED_PRODUCT_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'productName', label: 'Product', className: WORKSPACE_PRODUCT_IDENTITY_COL_CLASS },
  { id: 'status', label: 'Status' },
  { id: 'inventory', label: 'Inventory' },
];

export function defaultColumnIds(
  entityColumns: WorkspaceEntityColumnDef[],
  metricColumns: WorkspaceMetricColumnDef[] = WORKSPACE_METRIC_COLUMNS
): string[] {
  return [...entityColumns.map((c) => c.id), ...metricColumns.map((c) => c.key)];
}

/** Options for the Columns picker (entity + metric labels). */
export function workspaceColumnPickerOptions(
  entityColumns: WorkspaceEntityColumnDef[],
  metricColumns: WorkspaceMetricColumnDef[] = WORKSPACE_METRIC_COLUMNS
): { id: string; label: string }[] {
  return [
    ...entityColumns.map((c) => ({ id: c.id, label: c.label })),
    ...metricColumns.map((c) => ({ id: c.key, label: c.label })),
  ];
}

export function metricColumnPickerOptions(
  metricColumns: WorkspaceMetricColumnDef[] = WORKSPACE_METRIC_COLUMNS
): { id: string; label: string }[] {
  return metricColumns.map((c) => ({ id: c.key, label: c.label }));
}

export function metricColumnsByKey(
  columns: WorkspaceMetricColumnDef[] = WORKSPACE_METRIC_COLUMNS
): Map<string, WorkspaceMetricColumnDef> {
  return new Map(columns.map((c) => [c.key, c]));
}

export function entityColumnsById(
  columns: WorkspaceEntityColumnDef[]
): Map<string, WorkspaceEntityColumnDef> {
  return new Map(columns.map((c) => [c.id, c]));
}

/** Stable default id lists (module-level) so hooks don't see new array refs each render. */
export const PORTFOLIO_COLUMN_IDS = defaultColumnIds(PORTFOLIO_ENTITY_COLUMNS);
export const CAMPAIGN_COLUMN_IDS = defaultColumnIds(CAMPAIGN_ENTITY_COLUMNS);
export const AD_GROUP_COLUMN_IDS = defaultColumnIds(AD_GROUP_ENTITY_COLUMNS);
export const KEYWORD_COLUMN_IDS = defaultColumnIds(KEYWORD_ENTITY_COLUMNS);
export const AD_PRODUCT_COLUMN_IDS = defaultColumnIds(AD_PRODUCT_ENTITY_COLUMNS);
export const ADVERTISED_PRODUCT_COLUMN_IDS = defaultColumnIds(ADVERTISED_PRODUCT_ENTITY_COLUMNS);
export const METRIC_ONLY_COLUMN_IDS = WORKSPACE_METRIC_COLUMNS.map((c) => c.key);
